<template>
  <div class="messages-list-container">
    <!-- Search and filter bar -->
    <div class="search-bar mb-4">
      <div class="input-group">
        <input 
          type="text" 
          v-model="searchQuery"
          class="form-control" 
          placeholder="Buscar mensajes..."
          @input="filterMessages"
        >
        <button class="btn btn-primary">
          <i class="fas fa-search"></i>
        </button>
      </div>
    </div>

    <!-- Messages list -->
    <div class="messages-wrapper">
      <div v-if="isLoading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Cargando...</span>
        </div>
      </div>

      <div v-else-if="filteredMessages.length === 0" class="text-center py-5">
        <i class="fas fa-inbox fa-3x mb-3"></i>
        <p>No hay mensajes para mostrar</p>
      </div>

      <div v-else class="message-list">
        <div 
          v-for="message in filteredMessages" 
          :key="message.id" 
          class="message-card"
          :class="{ 'unread': !message.read }"
          @click="openMessage(message)"
        >
          <div class="message-header">
            <div class="user-info">
              <div class="avatar">{{ getInitials(message.customerName) }}</div>
              <div class="details">
                <h6 class="name">{{ message.customerName }}</h6>
                <span class="email">{{ message.customerEmail }}</span>
              </div>
            </div>
            <div class="date">{{ formatDate(message.createdAt) }}</div>
          </div>
          
          <div class="message-preview">
            <h6 class="subject">{{ message.subject }}</h6>
            <p class="preview-text">{{ truncateText(message.message, 120) }}</p>
          </div>

          <div class="message-actions">
            <button 
              class="btn btn-sm btn-primary"
              @click.stop="replyToMessage(message)"
            >
              <i class="fas fa-reply me-1"></i>
              Responder
            </button>
            <button 
              class="btn btn-sm btn-outline-danger"
              @click.stop="deleteMessage(message.id)"
            >
              <i class="fas fa-trash-alt"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Reply Modal -->
    <div v-if="showReplyModal" class="modal-overlay" @click="showReplyModal = false">
      <div class="reply-modal" @click.stop>
        <div class="modal-header">
          <h5>Responder a {{ selectedMessage?.customerName }}</h5>
          <button type="button" class="btn-close" @click="showReplyModal = false"></button>
        </div>
        <div class="modal-body">
          <div class="original-message mb-4">
            <small class="d-block text-muted mb-2">Mensaje original:</small>
            <div class="original-content">{{ selectedMessage?.message }}</div>
          </div>
          <div class="form-group">
            <label>Tu respuesta</label>
            <textarea 
              v-model="replyText" 
              class="form-control" 
              rows="4"
              placeholder="Escribe tu respuesta..."
            ></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-outline-secondary" @click="showReplyModal = false">
            Cancelar
          </button>
          <button 
            class="btn btn-primary"
            :disabled="!replyText.trim() || isSending"
            @click="sendReply"
          >
            <i class="fas fa-spinner fa-spin me-2" v-if="isSending"></i>
            {{ isSending ? 'Enviando...' : 'Enviar respuesta' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useToast } from 'vue-toastification'

const toast = useToast()

const messages = ref([])
const filteredMessages = ref([])
const searchQuery = ref('')
const isLoading = ref(true)
const showReplyModal = ref(false)
const selectedMessage = ref(null)
const replyText = ref('')
const isSending = ref(false)

const getInitials = (name) => {
  return name
    .split(' ')
    .map(part => part.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('')
}

const formatDate = (date) => {
  return new Date(date).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const truncateText = (text, length) => {
  if (text.length <= length) return text
  return text.substring(0, length) + '...'
}

const filterMessages = () => {
  const query = searchQuery.value.toLowerCase()
  filteredMessages.value = messages.value.filter(message => 
    message.customerName.toLowerCase().includes(query) ||
    message.customerEmail.toLowerCase().includes(query) ||
    message.subject.toLowerCase().includes(query) ||
    message.message.toLowerCase().includes(query)
  )
}

const openMessage = (message) => {
  if (!message.read) {
    message.read = true
    // TODO: Update read status in backend
  }
}

const replyToMessage = (message) => {
  selectedMessage.value = message
  replyText.value = ''
  showReplyModal.value = true
}

const sendReply = async () => {
  if (!replyText.value.trim()) return

  try {
    isSending.value = true
    // TODO: Implement reply sending logic
    await new Promise(resolve => setTimeout(resolve, 1000))
    
    toast.success('Respuesta enviada correctamente')
    showReplyModal.value = false
  } catch (error) {
    toast.error('Error al enviar la respuesta')
  } finally {
    isSending.value = false
  }
}

const deleteMessage = async (messageId) => {
  if (!confirm('¿Estás seguro de querer eliminar este mensaje?')) return

  try {
    // TODO: Implement delete logic
    messages.value = messages.value.filter(m => m.id !== messageId)
    filterMessages()
    toast.success('Mensaje eliminado correctamente')
  } catch (error) {
    toast.error('Error al eliminar el mensaje')
  }
}

onMounted(async () => {
  try {
    // TODO: Replace with actual API call
    await new Promise(resolve => setTimeout(resolve, 1000))
    
    messages.value = [
      {
        id: 1,
        customerName: 'Juan Pérez',
        customerEmail: 'juan@example.com',
        subject: 'Consulta sobre pedido #123',
        message: 'Hola, quisiera saber el estado de mi pedido...',
        createdAt: '2023-06-20T14:30:00',
        read: false
      },
      // Add more mock messages here
    ]
    
    filterMessages()
  } catch (error) {
    toast.error('Error al cargar los mensajes')
  } finally {
    isLoading.value = false
  }
})
</script>

<style scoped>
.messages-list-container {
  min-height: 400px;
}

.message-card {
  background: var(--black-color-2);
  border-radius: 1rem;
  padding: 1.5rem;
  margin-bottom: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.2s ease;
  cursor: pointer;
}

.message-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.message-card.unread {
  border-left: 4px solid var(--primary-color);
}

.message-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--primary-color);
  color: var(--black-color);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
}

.details .name {
  margin: 0;
  color: var(--white-color);
}

.details .email {
  font-size: 0.875rem;
  color: var(--white-color-2);
}

.date {
  font-size: 0.875rem;
  color: var(--white-color-2);
}

.message-preview .subject {
  color: var(--white-color);
  margin-bottom: 0.5rem;
}

.preview-text {
  color: var(--white-color-2);
  margin: 0;
  line-height: 1.5;
}

.message-actions {
  margin-top: 1rem;
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}

.reply-modal {
  width: 95%;
  max-width: 600px;
  background: var(--black-color-3);
  border-radius: 1rem;
  overflow: hidden;
}

.original-message {
  background: var(--black-color-2);
  padding: 1rem;
  border-radius: 0.5rem;
  color: var(--white-color-2);
}

.form-control {
  background: var(--black-color-2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--white-color);
}

.form-control:focus {
  background: var(--black-color-2);
  border-color: var(--primary-color);
  color: var(--white-color);
  box-shadow: none;
}
</style>
